import {
  string as stringType
} from '@thd-nucleus/data-sources';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import {
  INVALID_PROJECT_TEXT_MESSAGE,
  EMPTY_PROJECT_NAME_MESSAGE,
  INVALID_NAME_LENGTH_MESSAGE,
  EMPTY_PROJECT_TYPE_MESSAGE,
  EMPTY_PROPERTY_TYPE_MESSAGE,
  INVALID_TYPE_LENGTH_MESSAGE,
  INVALID_NOTE_LENGTH_MESSAGE,
  EMPTY_GROUP_NAME_MESSAGE,
  INVALID_GROUP_LENGTH_MESSAGE,
  STATUS_SESSION_STORAGE_KEY,
  INVALID_JOB_SITE_LENGTH_MESSAGE,
  INVALID_DELIVERY_INSTRUCTIONS_LENGTH_MESSAGE,
  INVALID_PHONE_NUMBER_LENGTH_MESSAGE,
  INVALID_PHONE_NUMBER_FORMAT_MESSAGE,
  INVALID_DELIVERY_CONTACT_NAME_LENGTH_MESSAGE,
  INVALID_SECURITY_CODE_LENGTH_MESSAGE,
  INVALID_LOCKBOX_CODE_LENGTH_MESSAGE,
  INVALID_LOCKBOX_LOCATION_LENGTH_MESSAGE,
  INVALID_JOB_SITE_HOURS_START_FORMAT_MESSAGE,
  INVALID_JOB_SITE_HOURS_END_FORMAT_MESSAGE,
  INVALID_ADDRESS_LENGTH_MESSAGE,
  INVALID_PROPERTY_TYPE_LENGTH_MESSAGE,
  PROJECT_NAME_KEY,
  PROJECT_TYPE_KEY,
  PROPERTY_TYPE_OTHER_KEY,
  PROJECT_DESCRIPTION_KEY,
  JOB_SITE_CONDITIONS_KEY,
  DELIVERY_INSTRUCTIONS_KEY,
  DELIVERY_CONTACT_NAME_KEY,
  DELIVERY_CONTACT_PHONE_KEY,
  SECURITY_CODE,
  LOCKBOX_CODE,
  LOCKBOX_LOCATION,
  JOB_SITE_HOURS_START,
  JOB_SITE_HOURS_END,
  PROPERTY_TYPE_KEY,
  PACKAGE_PLACEMENT_KEY,
  DELIVERY_TYPE_KEY,
  HAS_STAIRS_KEY,
  GROUP_NAME_KEY,
  ADDRESS_LINE_1_KEY,
  ADDRESS_LINE_2_KEY
} from './constants';

const defaultValidCharacters = /^([ [\]{}|a-z|A-Z|!@#$%^&*()0-9-_+,.?"':;\\]*)?$/;
const phoneNumberCharacter = /^[0-9]{10}$/;
const allCharacters = /^[\s\S]*$/;
const timeCharacters = /^(\d{1,2}:\d{2}\s?(AM|PM))?$/i;

const errorDictionary = {
  [PROJECT_NAME_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    blankMessage: EMPTY_PROJECT_NAME_MESSAGE,
    invalidLengthMessage: INVALID_NAME_LENGTH_MESSAGE,
    lengthLimit: 80,
    validCharacters: defaultValidCharacters,
  },
  [PROJECT_TYPE_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    blankMessage: EMPTY_PROJECT_TYPE_MESSAGE,
    invalidLengthMessage: INVALID_TYPE_LENGTH_MESSAGE,
    lengthLimit: 21,
    validCharacters: defaultValidCharacters,
  },
  [PROJECT_DESCRIPTION_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    invalidLengthMessage: INVALID_NOTE_LENGTH_MESSAGE,
    lengthLimit: 500,
    validCharacters: defaultValidCharacters,
  },
  [JOB_SITE_CONDITIONS_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    invalidLengthMessage: INVALID_JOB_SITE_LENGTH_MESSAGE,
    lengthLimit: 45,
    validCharacters: defaultValidCharacters,
  },
  [DELIVERY_INSTRUCTIONS_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    invalidLengthMessage: INVALID_DELIVERY_INSTRUCTIONS_LENGTH_MESSAGE,
    lengthLimit: 200,
    validCharacters: defaultValidCharacters,
  },
  [DELIVERY_CONTACT_NAME_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    invalidLengthMessage: INVALID_DELIVERY_CONTACT_NAME_LENGTH_MESSAGE,
    lengthLimit: 40,
    validCharacters: defaultValidCharacters,
  },
  [DELIVERY_CONTACT_PHONE_KEY]: {
    invalidCharMessage: INVALID_PHONE_NUMBER_FORMAT_MESSAGE,
    invalidLengthMessage: INVALID_PHONE_NUMBER_LENGTH_MESSAGE,
    lengthLimit: 10,
    validCharacters: phoneNumberCharacter,
  },
  [SECURITY_CODE]: {
    invalidCharMessage: '',
    invalidLengthMessage: INVALID_SECURITY_CODE_LENGTH_MESSAGE,
    lengthLimit: 8,
    validCharacters: allCharacters,
  },
  [LOCKBOX_CODE]: {
    invalidCharMessage: '',
    invalidLengthMessage: INVALID_LOCKBOX_CODE_LENGTH_MESSAGE,
    lengthLimit: 8,
    validCharacters: allCharacters,
  },
  [LOCKBOX_LOCATION]: {
    invalidCharMessage: '',
    invalidLengthMessage: INVALID_LOCKBOX_LOCATION_LENGTH_MESSAGE,
    lengthLimit: 25,
    validCharacters: allCharacters,
  },
  [JOB_SITE_HOURS_START]: {
    invalidCharMessage: INVALID_JOB_SITE_HOURS_START_FORMAT_MESSAGE,
    validCharacters: timeCharacters,
  },
  [JOB_SITE_HOURS_END]: {
    invalidCharMessage: INVALID_JOB_SITE_HOURS_END_FORMAT_MESSAGE,
    validCharacters: timeCharacters,
  },
  [PROPERTY_TYPE_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    validCharacters: defaultValidCharacters,
  },
  [PROPERTY_TYPE_OTHER_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    blankMessage: EMPTY_PROPERTY_TYPE_MESSAGE,
    invalidLengthMessage: INVALID_PROPERTY_TYPE_LENGTH_MESSAGE,
    lengthLimit: 21,
    validCharacters: defaultValidCharacters,
  },
  [PACKAGE_PLACEMENT_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    validCharacters: defaultValidCharacters,
  },
  [DELIVERY_TYPE_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    validCharacters: defaultValidCharacters,
  },
  [HAS_STAIRS_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    invalidLengthMessage: INVALID_TYPE_LENGTH_MESSAGE,
    lengthLimit: 3,
    validCharacters: defaultValidCharacters,
  },
  [GROUP_NAME_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    blankMessage: EMPTY_GROUP_NAME_MESSAGE,
    invalidLengthMessage: INVALID_GROUP_LENGTH_MESSAGE,
    lengthLimit: 50,
    validCharacters: defaultValidCharacters,
  },
  [ADDRESS_LINE_1_KEY]: {
    invalidCharMessage: INVALID_PROJECT_TEXT_MESSAGE,
    invalidLengthMessage: INVALID_ADDRESS_LENGTH_MESSAGE,
    lengthLimit: 60,
    validCharacters: defaultValidCharacters,
  },
};

const getValidCharacters = (key) => {
  return errorDictionary[key]?.validCharacters || defaultValidCharacters;
};

const getCheckIfBlank = (key) => {
  return !!errorDictionary[key]?.blankMessage;
};

const getIsLengthLimited = (key) => {
  return !!errorDictionary[key]?.lengthLimit;
};

const getInvalidTextMessage = (key) => {
  return errorDictionary[key].invalidCharMessage;
};

const getBlankMessage = (key) => {
  return errorDictionary[key].blankMessage;
};

const getInvalidLengthMessage = (key) => {
  return errorDictionary[key].invalidLengthMessage;
};

const getFieldLengthLimit = (key) => {
  return errorDictionary[key].lengthLimit;
};

const isBlank = (string) => !(string || '').replace(/\s/g, '');

export const validateField = ({ value, key }) => {
  let errorMessage = '';
  const normalizedValue = value == null ? '' : String(value);
  const isFieldRequired = getCheckIfBlank(key);
  const hasBlankValueWhenItShouldnt = isFieldRequired && isBlank(normalizedValue);

  // Optional fields with empty values should not be validated
  if (!isFieldRequired && isBlank(normalizedValue)) {
    return { isError: false, errorMessage: '' };
  }

  // Check required-blank before invalid-characters so empty fields get the right message
  if (hasBlankValueWhenItShouldnt) {
    return { isError: true, errorMessage: getBlankMessage(key) };
  }

  const hasInvalidCharacters = !getValidCharacters(key).test(normalizedValue);
  const isOverCharLimit = getIsLengthLimited(key)
    && typeof value === 'string' && value.length > getFieldLengthLimit(key);
  if (hasInvalidCharacters) {
    errorMessage = getInvalidTextMessage(key);
  } else if (isOverCharLimit) {
    errorMessage = getInvalidLengthMessage(key);
  }
  return { isError: !!errorMessage, errorMessage };
};

export const storeStatusChange = (statusValue) => {
  THDStorageUtils.set(SESSION_STORAGE, { key: STATUS_SESSION_STORAGE_KEY, value: statusValue });
};

export const PROJECT_SHAPE = {
  id: stringType(),
  name: stringType(),
  projectAccessType: stringType()
};

export const validateEstimatedStartDate = (value, endDate) => {
  if (endDate && (!value || new Date(value) >= new Date(endDate))) {
    return {
      isError: true,
      endDateErrorMessage: 'End date must be after start date',
    };
  }
  return { isError: false, endDateErrorMessage: '' };
};

export const validateEstimatedEndDate = (value, startDate) => {
  if ((new Date(value) <= new Date(startDate)) || !startDate) {
    return {
      isError: true,
      endDateErrorMessage: 'End date must be after start date'
    };
  }
  return { isError: false, endDateErrorMessage: '' };
};

export const triggerAnalytics = (event, payload) => {
  LIFE_CYCLE_EVENT_BUS.trigger(event, payload);
};

export const getYesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
};

export const formatDate = (date) => {
  const parsedDate = new Date(date);
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const normalizeToISODate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) return `${match[3]}-${match[1]}-${match[2]}`;
  return dateStr;
};

export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }
  return dateStr;
};

export const isValidUrl = (str) => {
  const pattern = new RegExp(
    '^([a-zA-Z]+:\\/\\/)?' // protocol
      + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'
      + '((\\d{1,3}\\.){3}\\d{1,3}))'
      + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'
      + '(\\?[;&a-z\\d%_.~+=-]*)?'
      + '(\\#[-a-z\\d_]*)?$',
    'i'
  );
  return pattern.test(str);
};

export const isWorkspaceDashboard = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.experienceMetadata?.name === 'my-homepage';
};

export const formatNoteDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).toLowerCase();

  if (isToday) {
    return `Today | ${timeStr}`;
  }

  const dateFormatted = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${dateFormatted} | ${timeStr}`;
};
